/**
 * Service de génération du formulaire de Vote Par Correspondance (VPC)
 *
 * Utilise docxtemplater pour générer un document Word à partir d'un template
 * conforme au format légal (Arrêté du 2 juillet 2020)
 */

import Docxtemplater from 'docxtemplater'
import PizZip from 'pizzip'
import { saveAs } from 'file-saver'
import { format, subDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import type { Meeting, Resolution, MeetingType } from '../types'

// ===== TYPES =====

export interface VPCOwnerData {
  id: string
  first_name: string
  last_name: string
  address?: string | null
  email?: string | null
}

export interface VPCUnitData {
  id: string
  unit_number: string
  distribution_key_ids: string[] // IDs des clés de répartition associées au lot
}

export interface VPCAgencyData {
  name: string
  address?: string | null
  email?: string | null
}

export interface VPCResidenceData {
  id: string
  name: string
  residence_key?: string | null
}

export interface VPCGenerationParams {
  meeting: Meeting
  owner: VPCOwnerData
  units: VPCUnitData[]
  resolutions: Resolution[]
  agency: VPCAgencyData
  residence: VPCResidenceData
}

export interface VPCFormData {
  // Contexte AG
  residence_name: string
  meeting_date_formatted: string
  meeting_time: string
  meeting_location: string
  meeting_type_label: string

  // Syndic
  agency_name: string
  agency_address: string
  agency_email: string

  // Copropriétaire
  owner_full_name: string
  owner_address: string
  owner_units: string

  // Méta
  form_code: string
  deadline_formatted: string
  current_year: number

  // Résolutions (pour le tableau)
  resolutions: VPCResolutionData[]

  // Texte légal
  legal_text: string
}

export interface VPCResolutionData {
  order_number: number
  title: string
  has_passerelle: boolean
  majority_label: string
}

// ===== CONSTANTS =====

const LEGAL_TEXT = `Conformément aux articles 17-1 A de la loi du 10 juillet 1965 et 9 et 9-1 du décret du 17 mars 1967 :
- Le formulaire de vote doit être reçu par le syndic au plus tard trois jours francs avant la date de l'assemblée générale.
- Les votes par correspondance sont pris en compte pour le calcul du quorum et des majorités.
- Le copropriétaire votant par correspondance est considéré comme présent pour le calcul de la majorité.
- En cas d'amendement d'une résolution, le vote par correspondance est compté comme défavorable.
- Le formulaire de vote doit être signé et paraphé sur chaque page.`

// ===== HELPER FUNCTIONS =====

/**
 * Génère le code formulaire unique
 * Format : VPC/[RESIDENCE_KEY]/[DATE_AG]/[LOTS]
 * Exemple : VPC/RES001/20251225/001-002
 */
export function generateFormCode(
  meeting: Meeting,
  units: VPCUnitData[],
  residence: VPCResidenceData
): string {
  const residenceKey = residence.residence_key || residence.id.slice(0, 6).toUpperCase()
  const meetingDate = new Date(meeting.meeting_date)
  const dateStr = format(meetingDate, 'yyyyMMdd')
  const lotsStr = units.map((u) => u.unit_number).join('-')

  return `VPC/${residenceKey}/${dateStr}/${lotsStr}`
}

/**
 * Calcule la date limite de réception (J-3 francs)
 * Un jour franc ne compte pas le jour de départ ni le jour d'arrivée
 */
export function calculateDeadline(meetingDate: Date | string): Date {
  const date = typeof meetingDate === 'string' ? new Date(meetingDate) : meetingDate
  // 3 jours francs = 4 jours calendaires avant la date de l'AG
  return subDays(date, 4)
}

/**
 * Prépare les données pour le template
 */
export function prepareVPCFormData(params: VPCGenerationParams): VPCFormData {
  const { meeting, owner, units, resolutions, agency, residence } = params

  const meetingDate = new Date(meeting.meeting_date)
  const deadline = calculateDeadline(meetingDate)

  // Formater les numéros de lots
  const unitsFormatted = units.map((u) => u.unit_number).join(', ')

  // Collecter toutes les clés de répartition des lots concernés
  const unitDistributionKeyIds = new Set<string>()
  for (const unit of units) {
    for (const keyId of unit.distribution_key_ids) {
      unitDistributionKeyIds.add(keyId)
    }
  }

  // Préparer les résolutions pour le tableau
  // Filtrer : uniquement les résolutions dont la clé de répartition concerne les lots du VPC
  const resolutionsData: VPCResolutionData[] = resolutions
    .filter((r) => r.status !== 'DRAFT') // Ne pas inclure les brouillons
    .filter((r) => {
      // Si la résolution n'a pas de clé de répartition, l'inclure (résolution générale)
      if (!r.distribution_key_id) return true
      // Sinon, vérifier que la clé fait partie des clés des lots
      return unitDistributionKeyIds.has(r.distribution_key_id)
    })
    .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    .map((r) => ({
      order_number: r.order_number ?? 0,
      title: r.title,
      has_passerelle: r.majority_type === 'absolute', // La passerelle 25-1 est toujours possible pour les art. 25
      majority_label: getMajorityShortLabel(r.majority_type),
    }))

  return {
    // Contexte AG
    residence_name: residence.name,
    meeting_date_formatted: format(meetingDate, 'EEEE d MMMM yyyy', { locale: fr }),
    meeting_time: meeting.start_time || '14:00',
    meeting_location: meeting.location || 'À préciser',
    meeting_type_label: getMeetingTypeLabel(meeting.meeting_type as MeetingType),

    // Syndic
    agency_name: agency.name,
    agency_address: agency.address || '',
    agency_email: agency.email || '',

    // Copropriétaire
    owner_full_name: `${owner.last_name.toUpperCase()} ${owner.first_name}`,
    owner_address: owner.address || '',
    owner_units: unitsFormatted || '-',

    // Méta
    form_code: generateFormCode(meeting, units, residence),
    deadline_formatted: format(deadline, 'd MMMM yyyy', { locale: fr }),
    current_year: new Date().getFullYear(),

    // Résolutions
    resolutions: resolutionsData,

    // Texte légal
    legal_text: LEGAL_TEXT,
  }
}

function getMeetingTypeLabel(type: MeetingType): string {
  const labels: Record<MeetingType, string> = {
    ordinary: 'Assemblée Générale Ordinaire',
    extraordinary: 'Assemblée Générale Extraordinaire',
    mixed: 'Assemblée Générale Mixte',
  }
  return labels[type] || 'Assemblée Générale'
}

function getMajorityShortLabel(majorityType: string | null): string {
  const labels: Record<string, string> = {
    simple: 'Art. 24',
    absolute: 'Art. 25',
    absolute_25_1: 'Art. 25-1',
    double: 'Art. 26',
    unanimity: 'Unanimité',
  }
  return labels[majorityType || 'simple'] || ''
}

// ===== MAIN GENERATION FUNCTION =====

/**
 * Génère le formulaire VPC et le télécharge
 */
export async function generateVPCForm(params: VPCGenerationParams): Promise<Blob> {
  // Charger le template
  const templateUrl = '/templates/vpc-template.docx'
  const response = await fetch(templateUrl)

  if (!response.ok) {
    throw new Error(`Template non trouvé: ${templateUrl}`)
  }

  const templateArrayBuffer = await response.arrayBuffer()

  // Créer le document avec PizZip
  const zip = new PizZip(templateArrayBuffer)
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    // Options pour éviter les erreurs sur les tags non trouvés
    nullGetter: () => '',
  })

  // Préparer les données
  const data = prepareVPCFormData(params)

  // Remplir le template
  doc.render(data)

  // Générer le blob
  const blob = doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })

  return blob
}

/**
 * Génère et télécharge le formulaire VPC
 */
export async function downloadVPCForm(params: VPCGenerationParams): Promise<void> {
  const blob = await generateVPCForm(params)

  // Générer le nom du fichier (remplacer / par _ pour le nom de fichier)
  const formCode = generateFormCode(params.meeting, params.units, params.residence)
  const fileName = `${formCode.replace(/\//g, '_')}.docx`

  // Télécharger
  saveAs(blob, fileName)
}
